import { Download, FileText, Image as ImageIcon, Loader2, Share2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import TableFilterBar from "@/components/table/TableFilterBar";
import TableSettingsGear, { TableColumnsPanel } from "@/components/table/TableSettingsGear";
import { Button } from "@/components/ui/button";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { useState, useMemo, useRef } from "react";
import html2canvas from "html2canvas";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import MemberNameCell from "@/components/MemberNameCell";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import jsPDF from "jspdf";

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
const MINISTRY_NOTE_PREFIX = "Ministry: ";
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";
const STATEMENT_PREVIEW_DIALOG_CLASS = "w-[calc(100vw-2rem)] max-w-3xl min-h-[75vh] max-h-[90vh] overflow-y-auto";

function fmt(v: number) {
  return "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getOrganizationFromNotes(notes: string | null) {
  if (!notes) return "";
  const firstLine = notes.split("\n")[0]?.trim() || "";
  if (firstLine.startsWith(MINISTRY_NOTE_PREFIX)) return firstLine.replace(MINISTRY_NOTE_PREFIX, "").trim();
  if (firstLine.startsWith(LEGACY_ORG_NOTE_PREFIX)) return firstLine.replace(LEGACY_ORG_NOTE_PREFIX, "").trim();
  return "";
}

function getOrganizationIdsFromNotes(notes: string | null) {
  if (!notes) return [] as string[];
  const lines = notes.split("\n").map((line) => line.trim());
  const idsLine = lines.find((line) =>
    line.startsWith(MINISTRY_IDS_NOTE_PREFIX) || line.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
  );
  if (!idsLine) return [] as string[];
  return idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((id) => id.trim())
    .filter(Boolean);
}

type OrganizationNode = {
  id: string;
  name: string;
  parent_id: string | null;
};

async function loadImageAsBase64(url: string): Promise<{ data: string; width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

interface ChurchInfo {
  church_name: string;
  logo_url: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip_code: string | null;
  phone: string | null;
  email: string | null;
}

function getChurchInfoLines(church: ChurchInfo) {
  const lines: string[] = [];
  const addressParts = [church.address, [church.city, church.state, church.zip_code].filter(Boolean).join(", "), church.country].filter(Boolean);
  if (addressParts.length > 0) lines.push(addressParts.join(", "));
  if (church.phone) lines.push(`Phone: ${church.phone}`);
  if (church.email) lines.push(`Email: ${church.email}`);
  return lines;
}

async function generatePDF(
  memberName: string,
  year: number,
  records: any[],
  church: ChurchInfo
) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginL = 20;
  const marginR = 20;
  const contentW = pageW - marginL - marginR;
  let y = 20;

  let logoHeight = 0;
  if (church.logo_url) {
    const logoInfo = await loadImageAsBase64(church.logo_url);
    if (logoInfo) {
      const maxSize = 18; // 18mm max dimension
      const aspectRatio = logoInfo.width / logoInfo.height;
      let logoWidth = maxSize;
      let logoH = maxSize;
      
      if (aspectRatio > 1) {
        // Wide logo: constrain height
        logoH = maxSize / aspectRatio;
      } else {
        // Tall logo: constrain width
        logoWidth = maxSize * aspectRatio;
      }
      
      try { 
        doc.addImage(logoInfo.data, "PNG", marginL, y, logoWidth, logoH); 
      } catch {}
      logoHeight = logoH;
    }
  }

  const headerX = church.logo_url ? marginL + 22 : marginL;
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(church.church_name, headerX, y + 7);

  const infoLines = getChurchInfoLines(church);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100);
  infoLines.forEach((line, i) => { doc.text(line, headerX, y + 13 + i * 4); });

  y += Math.max(22, 13 + infoLines.length * 4 + 4);
  doc.setDrawColor(200);
  doc.setLineWidth(0.5);
  doc.line(marginL, y, pageW - marginR, y);
  y += 10;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0);
  doc.text("CONTRIBUTION STATEMENT", marginL, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60);
  doc.text(`Year: ${year}`, marginL, y); y += 5;
  doc.text(`Source: ${memberName}`, marginL, y); y += 5;
  doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`, marginL, y);
  y += 10;

  const colX = { date: marginL, category: marginL + 24, org: marginL + 58, method: marginL + 100, amount: pageW - marginR };
  doc.setFillColor(245, 245, 245);
  doc.rect(marginL, y - 4, contentW, 8, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(80);
  doc.text("Date", colX.date, y);
  doc.text("Category", colX.category, y);
  doc.text("Ministry", colX.org, y);
  doc.text("Payment Method", colX.method, y);
  doc.text("Amount", colX.amount, y, { align: "right" });
  y += 7;

  const sorted = [...records].sort((a, b) => a.date.localeCompare(b.date));
  const total = sorted.reduce((s, r) => s + Number(r.amount), 0);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(30);
  doc.setFontSize(8);
  sorted.forEach((r) => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.text(fmtDate(r.date), colX.date, y);
    doc.text(r.category || "", colX.category, y);
    doc.text(r.organization || "—", colX.org, y);
    doc.text(r.payment_method || "—", colX.method, y);
    doc.text(fmt(Number(r.amount)), colX.amount, y, { align: "right" });
    y += 6;
  });

  y += 2;
  doc.setDrawColor(200);
  doc.line(marginL, y, pageW - marginR, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("TOTAL", marginL, y);
  doc.text(fmt(total), colX.amount, y, { align: "right" });
  y += 12;

  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text("This statement is provided for tax and record-keeping purposes.", marginL, y);
  y += 5;
  doc.text("No goods or services were provided in exchange for these contributions.Thank you for your generous contributions.", marginL, y);

  return doc;
}

function buildEmailBody(memberName: string, year: number, records: any[]) {
  const sorted = [...records].sort((a, b) => a.date.localeCompare(b.date));
  const total = sorted.reduce((s, r) => s + Number(r.amount), 0);
  const lines = [
    `Contribution Statement for ${memberName} — ${year}`,
    "",
    ...sorted.map((r) => `${fmtDate(r.date)}  ${r.category}  ${fmt(Number(r.amount))}`),
    "",
    `Total: ${fmt(total)}`,
    "",
    "This statement is provided for tax and record-keeping purposes.",
    "No goods or services were provided in exchange for these contributions.Thank you for your generous contributions.",
  ];
  return lines.join("\n");
}

// ── Statement Preview Dialog ──
function StatementPreview({
  member,
  year,
  church,
  open,
  onClose,
  onDownload,
  onShare,
  downloading,
  sharing,
}: {
  member: { name: string; total: number; records: any[] };
  year: string;
  church: ChurchInfo;
  open: boolean;
  onClose: () => void;
  onDownload: () => void;
  onShare: () => void;
  downloading: boolean;
  sharing: boolean;
}) {
  const sorted = [...member.records].sort((a, b) => a.date.localeCompare(b.date));
  const infoLines = getChurchInfoLines(church);
  const contentRef = useRef<HTMLDivElement>(null);
  const [downloadingJpeg, setDownloadingJpeg] = useState(false);

  const handleDownloadJpeg = async () => {
    const el = contentRef.current;
    if (!el) return;
    setDownloadingJpeg(true);
    try {
      // US Letter: 8.5" x 11" at 96dpi = 816 x 1056px
      const letterW = 816;
      const letterH = 1056;
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.width = `${letterW}px`;
      clone.style.minHeight = `${letterH}px`;
      clone.style.padding = "48px";
      clone.style.position = "fixed";
      clone.style.left = "-9999px";
      clone.style.top = "0";
      clone.style.backgroundColor = "#ffffff";
      document.body.appendChild(clone);

      const canvas = await html2canvas(clone, {
        width: letterW,
        height: Math.max(letterH, clone.scrollHeight),
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      document.body.removeChild(clone);

      const link = document.createElement("a");
      link.download = `${member.name.replace(/\s+/g, "_")}_${year}_Statement.jpeg`;
      link.href = canvas.toDataURL("image/jpeg", 0.95);
      link.click();
      
    } catch {
      toast.error("Failed to generate JPEG");
    } finally {
      setDownloadingJpeg(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={STATEMENT_PREVIEW_DIALOG_CLASS}>
        <DialogHeader>
          <DialogTitle className="font-heading">Contribution Statement</DialogTitle>
        </DialogHeader>

        <div ref={contentRef} className="bg-white p-4">
        {/* Church header */}
        <div className="flex items-start gap-4 pb-4 border-b border-border">
          {church.logo_url && (
            <img src={church.logo_url} alt="Church logo" className="h-14 w-auto max-w-24 rounded-lg object-contain" />
          )}
          <div>
            <h2 className="text-lg font-bold text-foreground">{church.church_name}</h2>
            {infoLines.map((line, i) => (
              <p key={i} className="text-xs text-foreground">{line}</p>
            ))}
          </div>
        </div>

        {/* Statement info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-3 text-sm">
          <div>
            <span className="text-foreground text-xs font-medium">Source</span>
            <p className="font-medium text-foreground">{member.name}</p>
          </div>
          <div>
            <span className="text-foreground text-xs font-medium">Year</span>
            <p className="font-medium text-foreground">{year}</p>
          </div>
          <div>
            <span className="text-foreground text-xs font-medium">Total</span>
            <p className="font-bold text-foreground">{fmt(member.total)}</p>
          </div>
        </div>

        {/* Records table */}
        <div className="border border-border rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="bg-secondary/30 border-b border-border">
                <th className="text-left px-4 py-2 text-xs font-bold text-foreground uppercase">Date</th>
                <th className="text-left px-4 py-2 text-xs font-bold text-foreground uppercase">Category</th>
                <th className="text-left px-4 py-2 text-xs font-bold text-foreground uppercase">Ministry</th>
                <th className="text-left px-4 py-2 text-xs font-bold text-foreground uppercase">Method</th>
                <th className="text-right px-4 py-2 text-xs font-bold text-foreground uppercase">Amount</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={r.id || i} className="border-b border-border/50">
                  <td className="px-4 py-2.5 text-foreground text-sm">{fmtDate(r.date)}</td>
                  <td className="px-4 py-2.5 text-foreground text-sm">{r.category}</td>
                  <td className="px-4 py-2.5 text-foreground text-sm">{r.organization || "—"}</td>
                  <td className="px-4 py-2.5 text-foreground text-sm">{r.payment_method || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-foreground text-sm">{fmt(Number(r.amount))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-secondary/20">
                <td colSpan={4} className="px-4 py-2.5 font-bold text-foreground">Total</td>
                <td className="px-4 py-2.5 text-right font-bold text-foreground">{fmt(member.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-xs text-foreground italic pt-2">
          This statement is provided for tax and record-keeping purposes. No goods or services were provided in exchange for these contributions.Thank you for your generous contributions.
        </p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            variant="outline"
            onClick={onShare}
            disabled={sharing}
          >
            {sharing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sharing...</>
            ) : (
              <><Share2 className="w-4 h-4 mr-2" /> Share</>
            )}
          </Button>
          <Button onClick={onDownload} disabled={downloading}>
            {downloading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> PDF</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Contribution Statements Component ──
export default function ContributionStatements() {
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(String(currentYear));
  const [downloading, setDownloading] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<{ name: string; total: number; records: any[] } | null>(null);
  type ContribColKey = "member" | "total" | "lastDate" | "records" | "statement";
  const [visibleContribCols, setVisibleContribCols] = useState<Set<ContribColKey>>(new Set(["member", "total", "lastDate", "records", "statement"]));
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [contribSortCol, setContribSortCol] = useState<string>("total");
  const [contribSortDir, setContribSortDir] = useState<"asc" | "desc" | null>("desc");
  const toggleContribCol = (key: ContribColKey) => {
    setVisibleContribCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  };
  const contribColumns: { key: ContribColKey; label: string; sortField: string }[] = [
    { key: "member", label: "Source", sortField: "name" },
    { key: "total", label: "Total Giving", sortField: "total" },
    { key: "lastDate", label: "Last Contribution", sortField: "lastDate" },
    { key: "records", label: "# Records", sortField: "recordCount" },
    { key: "statement", label: "Statement", sortField: "" },
  ];
  const toggleContribSort = (field: string) => {
    if (!field) return;
    if (contribSortCol === field) {
      if (contribSortDir === "asc") setContribSortDir("desc");
      else if (contribSortDir === "desc") { setContribSortCol(""); setContribSortDir(null); }
    } else { setContribSortCol(field); setContribSortDir("asc"); }
  };
  const ContribSortIcon = ({ field }: { field: string }) => {
    if (!field) return null;
    if (contribSortCol !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return contribSortDir === "asc" ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };
  const { profile } = useChurch();
  const { role } = useAuth();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess } = usePermissions();
  const financeScoped = role !== "admin" && hasFinanceScopedAccess();
  const allowedFinanceOrgIds = getFinanceOrganizationIds();

  const { data: records, isLoading } = useQuery({
    queryKey: ["finance_records", profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("*")
        .eq("church_id", profile!.id)
        .order("date", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "contribution_scope", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, parent_id")
        .eq("church_id", profile!.id);
      if (error) throw error;
      return (data || []) as OrganizationNode[];
    },
    enabled: !!profile?.id,
  });

  const scopedOrgSet = useMemo(() => {
    if (!financeScoped) return null;

    const byParent = new Map<string, string[]>();
    organizations.forEach((org) => {
      if (!org.parent_id) return;
      const current = byParent.get(org.parent_id) || [];
      current.push(org.id);
      byParent.set(org.parent_id, current);
    });

    const visited = new Set<string>();
    const queue = [...allowedFinanceOrgIds];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const children = byParent.get(currentId) || [];
      children.forEach((childId) => {
        if (!visited.has(childId)) queue.push(childId);
      });
    }

    return visited;
  }, [financeScoped, organizations, allowedFinanceOrgIds]);

  const organizationNameToIds = useMemo(() => {
    const map = new Map<string, string[]>();
    organizations.forEach((org) => {
      const key = org.name.toLowerCase();
      const existing = map.get(key) || [];
      existing.push(org.id);
      map.set(key, existing);
    });
    return map;
  }, [organizations]);

  const recordsByScope = useMemo(() => {
    if (!records) return [];
    if (!financeScoped || !scopedOrgSet) return records;

    return records.filter((record: any) => {
      const orgIdsFromNotes = getOrganizationIdsFromNotes(record.notes);
      if (orgIdsFromNotes.length > 0) {
        return orgIdsFromNotes.some((orgId) => scopedOrgSet.has(orgId));
      }

      const orgPath = getOrganizationFromNotes(record.notes);
      if (!orgPath) return false;
      const orgNames = orgPath.split(">").map((part) => part.trim().toLowerCase()).filter(Boolean);
      return orgNames.some((name) => {
        const matchedIds = organizationNameToIds.get(name) || [];
        return matchedIds.some((id) => scopedOrgSet.has(id));
      });
    });
  }, [records, financeScoped, scopedOrgSet, organizationNameToIds]);

  const memberSummaries = useMemo(() => {
    const yearRecords = recordsByScope.filter((r: any) => r.member_name && r.date.startsWith(year));
    const map: Record<string, { name: string; total: number; lastDate: string; records: any[] }> = {};
    yearRecords.forEach((r) => {
      const name = r.member_name!;
      if (!map[name]) map[name] = { name, total: 0, lastDate: r.date, records: [] };
      map[name].total += Number(r.amount);
      map[name].records.push({ ...r, organization: getOrganizationFromNotes(r.notes) });
      if (r.date > map[name].lastDate) map[name].lastDate = r.date;
    });
    let result = Object.values(map)
      .filter((m) => m.name.toLowerCase().includes(search.toLowerCase()));
    if (contribSortCol && contribSortDir) {
      result = result.sort((a, b) => {
        if (contribSortCol === "total") { return contribSortDir === "asc" ? a.total - b.total : b.total - a.total; }
        if (contribSortCol === "recordCount") { return contribSortDir === "asc" ? a.records.length - b.records.length : b.records.length - a.records.length; }
        const aVal = (a as any)[contribSortCol] ?? "";
        const bVal = (b as any)[contribSortCol] ?? "";
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
        return contribSortDir === "asc" ? cmp : -cmp;
      });
    } else {
      result.sort((a, b) => b.total - a.total);
    }
    return result;
  }, [recordsByScope, year, search, contribSortCol, contribSortDir]);

  const churchInfo: ChurchInfo = {
    church_name: profile?.church_name || "Church",
    logo_url: profile?.logo_url || null,
    address: profile?.address || null,
    city: profile?.city || null,
    state: profile?.state || null,
    country: profile?.country || null,
    zip_code: profile?.zip_code || null,
    phone: profile?.phone || null,
    email: profile?.email || null,
  };

  const handleDownload = async (m: { name: string; records: any[] }) => {
    setDownloading(m.name);
    try {
      const doc = await generatePDF(m.name, Number(year), m.records, churchInfo);
      doc.save(`${m.name.replace(/\s+/g, "_")}_${year}_Statement.pdf`);
      
    } catch {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(null);
    }
  };

  const [sharing, setSharing] = useState(false);

  const handleShare = async (m: { name: string; records: any[] }) => {
    setSharing(true);
    try {
      const doc = await generatePDF(m.name, Number(year), m.records, churchInfo);
      const fileName = `${m.name.replace(/\s+/g, "_")}_${year}_Statement.pdf`;
      const blob = doc.output("blob");

      // Mobile: use native share with PDF file
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], fileName, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `${m.name} — ${year} Contribution Statement`,
            files: [file],
          });
          setSharing(false);
          return;
        }
      }

      // Desktop: open mailto with pre-filled content
      const subject = encodeURIComponent(`${m.name} — ${year} Contribution Statement`);
      const body = encodeURIComponent(buildEmailBody(m.name, Number(year), m.records));
      window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
    } catch (err: any) {
      if (err?.name !== "AbortError") toast.error("Share failed");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="space-y-4">
      <TableFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search source..."
        extraControls={
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />

      {columnsOpen && (
        <TableColumnsPanel
          columns={contribColumns.map(c => ({ key: c.key, label: c.label }))}
          visibleColumns={visibleContribCols as Set<string>}
          onToggle={(key) => toggleContribCol(key as ContribColKey)}
          onClose={() => setColumnsOpen(false)}
        />
      )}

      <div className="bg-card rounded-2xl border border-border/60 p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : memberSummaries.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No source contributions found for {year}.</p>
            <p className="text-xs text-muted-foreground mt-1">Only records with a source name are shown here.</p>
          </div>
        ) : (
          <>
          <div className="flex items-center justify-between px-4 py-1.5 border-b border-border/40">
            <p className="text-xs text-muted-foreground font-medium">{memberSummaries.length} source{memberSummaries.length !== 1 ? "s" : ""}</p>
            <TableSettingsGear
              columns={contribColumns.map(c => ({ key: c.key, label: c.label }))}
              visibleColumns={visibleContribCols as Set<string>}
              onToggle={(key) => toggleContribCol(key as ContribColKey)}
              open={columnsOpen}
              onOpenChange={setColumnsOpen}
            />
          </div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[400px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                {contribColumns.filter(c => visibleContribCols.has(c.key)).map((col) => (
                  <th
                    key={col.key}
                    className={`text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-2.5 ${col.sortField ? "cursor-pointer select-none hover:text-foreground transition-colors" : ""} ${col.key === "statement" ? "text-right" : "text-left"}`}
                    onClick={() => col.sortField && toggleContribSort(col.sortField)}
                  >
                    <span className="inline-flex items-center">
                      {col.key === "total" ? `Total Giving (${year})` : col.label}
                      <ContribSortIcon field={col.sortField} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {memberSummaries.map((m) => (
                <tr
                  key={m.name}
                  className="h-[36px] border-b border-border/50 hover:bg-primary/10 hover:shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.18)] transition-colors cursor-pointer"
                  onClick={() => setSelectedMember(m)}
                >
                  {visibleContribCols.has("member") && (
                    <td className="px-4 py-0.5 text-sm font-medium text-foreground">
                      <MemberNameCell name={m.name} size="sm" iconStyle="avatar" />
                    </td>
                  )}
                  {visibleContribCols.has("total") && <td className="px-4 py-0.5 text-sm text-foreground">{fmt(m.total)}</td>}
                  {visibleContribCols.has("lastDate") && <td className="px-4 py-0.5 text-sm text-muted-foreground">{fmtDate(m.lastDate)}</td>}
                  {visibleContribCols.has("records") && <td className="px-4 py-0.5 text-sm text-muted-foreground">{m.records.length}</td>}
                  {visibleContribCols.has("statement") && (
                    <td className="px-4 py-0.5 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled={downloading === m.name}
                        onClick={(e) => { e.stopPropagation(); handleDownload(m); }}
                      >
                        {downloading === m.name ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> PDF</>
                        ) : (
                          <><Download className="w-4 h-4 mr-2" /> PDF</>
                        )}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>

      {selectedMember && (
        <StatementPreview
          member={selectedMember}
          year={year}
          church={churchInfo}
          open={!!selectedMember}
          onClose={() => setSelectedMember(null)}
          onDownload={() => handleDownload(selectedMember)}
          onShare={() => handleShare(selectedMember)}
          downloading={downloading === selectedMember.name}
          sharing={sharing}
        />
      )}
    </div>
  );
}
